import type { AdminAiScanReviewTreeProps } from "../../models/admin-ai-scans.models";
import { adminAiScansStyles } from "../../styles/admin-ai-scans.styles";
import { getAdminAiScanReviewSurfaces } from "../../utils/admin-ai-scan-review.utils";
import {
  getAdminAiScanFindingSeverity,
  getAdminAiScanObject,
  getAdminAiScanObjects,
  getAdminAiScanStrings,
  getAdminAiScanText,
} from "../../utils/admin-ai-scans.utils";
import AdminAiScanFeatureGroup from "./AdminAiScanFeatureGroup";
import AdminAiScanFinding from "./AdminAiScanFinding";
import AdminAiScanJsonDetails from "./AdminAiScanJsonDetails";
import AdminAiScanTreeSummary from "./AdminAiScanTreeSummary";

// Present findings under their first associated feature while retaining the complete inventory tree.
// Unmatched findings stay visible instead of disappearing when a scan and inventory differ.
const AdminAiScanReviewTree = ({
  inventory,
  report,
  featureReviewKey,
  findingFields,
  expansion,
  severity,
}: AdminAiScanReviewTreeProps) => {
  const { surfaces, unassignedFindings } = getAdminAiScanReviewSurfaces(inventory, report, featureReviewKey);
  const visibleFindings = (findings: ReturnType<typeof getAdminAiScanObjects>) =>
    findings.filter((finding) => !severity || getAdminAiScanFindingSeverity(finding) === severity);

  return (
    <div data-test="admin-ai-scan-review-tree">
      {surfaces.map((surface) => (
        <details key={surface.id} open={expansion !== "closed"} className={adminAiScansStyles.treeBranch}>
          <AdminAiScanTreeSummary
            label={surface.label}
            id={surface.id}
            kind={surface.kind}
            meta={`${surface.groups.length} feature groups`}
            counts={[
              { label: "Findings", value: visibleFindings(surface.groups.flatMap((group) => group.findings)).length },
            ]}
          />
          <div className={adminAiScansStyles.treeChildren}>
            {surface.groups.map((group) => (
              <AdminAiScanFeatureGroup
                key={group.id}
                group={group}
                hasReport={Boolean(report)}
                findingFields={findingFields}
                expansion={expansion}
                severity={severity}
              />
            ))}
          </div>
        </details>
      ))}
      {unassignedFindings.length > 0 && (
        <section className="space-y-2 p-3" data-test="admin-ai-scan-unassigned-findings">
          <h4 className="py-2 text-sm font-semibold">
            Findings outside current inventory ({unassignedFindings.length})
          </h4>
          {visibleFindings(unassignedFindings).map((finding, index) => (
            <AdminAiScanFinding
              key={`${getAdminAiScanText(finding.id)}:${index}`}
              finding={finding}
              findingFields={findingFields}
              expanded={expansion === "all"}
            />
          ))}
        </section>
      )}
      {report && (
        <div className="space-y-2 border-t border-slate-200 px-4 py-3 dark:border-gray-600">
          <p className={adminAiScansStyles.muted}>
            Coverage: {getAdminAiScanStrings(report.reviewedFeatureIds).length} reviewed ·{" "}
            {getAdminAiScanStrings(report.weaklyReviewedFeatureIds).length} weak ·{" "}
            {getAdminAiScanStrings(report.unreviewedFeatureIds).length} unreviewed. Zero findings alone does not
            establish completed review.
          </p>
          <AdminAiScanJsonDetails
            label="Summary and review notes"
            value={report.summary ?? getAdminAiScanObject(report.boReview)}
          />
          <AdminAiScanJsonDetails label="Remediations and references" value={report.remediations ?? []} />
          <AdminAiScanJsonDetails label="Human review needed" value={report.humanReviewNeeded ?? []} />
          <AdminAiScanJsonDetails label="Complete original report" value={report} />
        </div>
      )}
    </div>
  );
};

export default AdminAiScanReviewTree;
